import React, { useRef, useEffect, useState } from 'react';

const GrantSteampunkEngine = ({ 
  onEnergyGenerated = () => {}, 
  onFormulaDiscovered = () => {},
  className = "" 
}) => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const [isRunning, setIsRunning] = useState(true);
  
  // Grant's 7-Dimensional Variables
  const [dimensions, setDimensions] = useState({
    r: 1.618, // Radial distance (Golden ratio)
    theta: 0, // Theta angle (X-axis rotation)
    phi: 0, // Phi angle (Y-axis rotation)
    t: 1.0, // Time dimension (Z-axis flow)
    f: 528, // Sacred frequency
    u: 1.0, // Universal constant
    infinity: 3.14159 // Mathematical constant (π)
  });
  
  const [machineState, setMachineState] = useState({
    power: 75,
    resonance: 0,
    steamPressure: 60,
    energyOutput: 0,
    discoveredFormulas: [],
    dimensionalHarmony: 0,
    evolutionLevel: 1,
    particleCount: 0
  });

  // Grant's Mathematical Formulas
  const grantFormulas = [
    {
      id: 'dynamic_frequency',
      name: '5-D Dynamic Frequency',
      formula: 'F₅ = f⋅sin(θ)⋅cos(φ)⋅r^(t/u)',
      variables: ['f', 'theta', 'phi', 'r', 't', 'u'],
      unlock: { f: 528, theta: 45, phi: 30, r: 1.618, t: 1.5, u: 1.0 },
      tolerance: 0.1,
      description: 'Core frequency that bridges dimensions',
      energyBonus: 500
    },
    {
      id: 'tonal_spiral',
      name: 'Tonal Spiral Equation',
      formula: 'S = r⋅e^(iθ)⋅√(f⋅t)',
      variables: ['r', 'theta', 'f', 't'],
      unlock: { r: 2.718, theta: 144, f: 432, t: 2.0 },
      tolerance: 0.15,
      description: 'Sacred geometry spiral with tonal resonance',
      energyBonus: 400
    },
    {
      id: 'oscillating_system',
      name: 'Oscillating System Matrix',
      formula: 'O = sin(φ⋅t)⋅cos(θ⋅f)⋅r^u',
      variables: ['phi', 't', 'theta', 'f', 'r', 'u'],
      unlock: { phi: 60, t: 1.618, theta: 120, f: 528, r: 1.414, u: 1.25 },
      tolerance: 0.12,
      description: 'Dual-state oscillation between consciousness levels',
      energyBonus: 600
    },
    {
      id: 'universal_framework',
      name: '7-D Universal Framework',
      formula: 'U₇ = ∑(r^n⋅sin(nθ+φ)⋅f^(t/infinity))',
      variables: ['r', 'theta', 'phi', 'f', 't', 'infinity'],
      unlock: { r: 3.14159, theta: 216, phi: 108, f: 741, t: 2.618, infinity: 3.14159 },
      tolerance: 0.08,
      description: 'Complete universal dimensional integration',
      energyBonus: 1000
    }
  ];

  // Main 3D Engine Animation
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const width = canvas.width;
    const height = canvas.height;
    const centerX = width / 2;
    const centerY = height / 2;
    
    let time = 0;

    const drawEngine = () => {
      // Steampunk brass background
      const bgGradient = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, Math.max(width, height) / 2);
      bgGradient.addColorStop(0, 'rgba(139, 69, 19, 0.95)');
      bgGradient.addColorStop(0.3, 'rgba(160, 82, 45, 0.9)');
      bgGradient.addColorStop(0.7, 'rgba(101, 67, 33, 0.92)');
      bgGradient.addColorStop(1, 'rgba(62, 39, 35, 1)');
      ctx.fillStyle = bgGradient;
      ctx.fillRect(0, 0, width, height);

      // === AMAZING MOVING BALLS TOWARD CENTER ===
      const numBalls = 50;
      let activeBalls = 0;
      
      for (let i = 0; i < numBalls; i++) {
        const ballTime = time * 0.01 + i * 0.1;
        const ballAngle = (i * 2 * Math.PI) / numBalls + ballTime;
        
        // 3D spherical coordinates for each ball
        const ballR = 250 - (ballTime % 10) * 25; // Balls spiral inward
        const ballTheta = ballAngle + dimensions.theta * Math.PI / 180;
        const ballPhi = Math.sin(ballTime) * Math.PI + dimensions.phi * Math.PI / 180;
        
        // Convert 3D spherical to Cartesian coordinates
        const x3d = ballR * Math.sin(ballPhi) * Math.cos(ballTheta);
        const y3d = ballR * Math.sin(ballPhi) * Math.sin(ballTheta);
        const z3d = ballR * Math.cos(ballPhi) + dimensions.t * 50;
        
        // Project 3D to 2D with perspective
        const perspective = 300 / (300 + z3d);
        const ballX = centerX + x3d * perspective;
        const ballY = centerY + y3d * perspective;
        
        // Only draw balls within canvas bounds and moving toward center
        if (ballR > 30 && ballX >= 0 && ballX <= width && ballY >= 0 && ballY <= height) {
          activeBalls++;
          
          // Ball size increases as it gets closer (perspective)
          const ballSize = (8 + ballR / 30) * perspective;
          
          // Dynamic ball color based on dimensional parameters
          const hue = (i * 360 / numBalls + dimensions.f / 10) % 360;
          const ballAlpha = (machineState.power / 100) * perspective;
          
          // Create radial gradient for each ball
          const ballGradient = ctx.createRadialGradient(ballX, ballY, 0, ballX, ballY, ballSize);
          ballGradient.addColorStop(0, `hsla(${hue}, 100%, 70%, ${ballAlpha})`);
          ballGradient.addColorStop(0.7, `hsla(${hue}, 80%, 50%, ${ballAlpha * 0.8})`);
          ballGradient.addColorStop(1, `hsla(${hue}, 60%, 30%, 0)`);
          
          ctx.fillStyle = ballGradient;
          ctx.beginPath();
          ctx.arc(ballX, ballY, ballSize, 0, 2 * Math.PI);
          ctx.fill();
          
          // Glowing effect for each ball
          ctx.shadowColor = `hsla(${hue}, 100%, 60%, ${ballAlpha})`;
          ctx.shadowBlur = ballSize * 2;
          ctx.fill();
          ctx.shadowBlur = 0;
          
          // Energy trails behind moving balls
          if (ballR < 150) {
            const trailLength = 5;
            for (let t = 1; t <= trailLength; t++) {
              const trailR = ballR + t * 10;
              const trailX3d = trailR * Math.sin(ballPhi) * Math.cos(ballTheta);
              const trailY3d = trailR * Math.sin(ballPhi) * Math.sin(ballTheta);
              const trailZ3d = trailR * Math.cos(ballPhi) + dimensions.t * 50;
              
              const trailPerspective = 300 / (300 + trailZ3d);
              const trailX = centerX + trailX3d * trailPerspective;
              const trailY = centerY + trailY3d * trailPerspective;
              
              if (trailX >= 0 && trailX <= width && trailY >= 0 && trailY <= height) {
                const trailSize = 3 * trailPerspective * (1 - t / trailLength);
                const trailAlpha = ballAlpha * (1 - t / trailLength) * 0.6;
                
                ctx.fillStyle = `hsla(${hue}, 100%, 80%, ${trailAlpha})`;
                ctx.beginPath();
                ctx.arc(trailX, trailY, trailSize, 0, 2 * Math.PI);
                ctx.fill();
              }
            }
          }
        }
      }
      
      setMachineState(prev => ({ ...prev, particleCount: activeBalls }));

      // === CENTRAL GOLDEN COMPUTATIONAL CORE ===
      const coreSize = 80 + Math.sin(time * 0.02) * 10;
      const coreGradient = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, coreSize);
      coreGradient.addColorStop(0, `rgba(255, 215, 0, ${machineState.power / 100})`);
      coreGradient.addColorStop(0.4, `rgba(255, 140, 0, ${machineState.power / 150})`);
      coreGradient.addColorStop(0.8, `rgba(184, 134, 11, ${machineState.power / 200})`);
      coreGradient.addColorStop(1, 'rgba(139, 69, 19, 0.3)');
      
      ctx.fillStyle = coreGradient;
      ctx.beginPath();
      ctx.arc(centerX, centerY, coreSize, 0, 2 * Math.PI);
      ctx.fill();
      
      time += 1;
    };

    const animate = () => {
      if (isRunning) {
        drawEngine();
        animationRef.current = requestAnimationFrame(animate);
      } else {
        drawEngine();
      }
    };

    animate();

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [dimensions, isRunning, machineState.power]);

  const updateDimension = (dim, value) => {
    setDimensions(prev => ({ ...prev, [dim]: value }));
  };

  return (
    <div className="w-full">
      <div className="relative">
        <canvas
          ref={canvasRef}
          width={700}
          height={500}
          className="w-full h-auto border-4 border-yellow-600/40 rounded-lg"
          style={{
            background: 'radial-gradient(circle, rgba(139,69,19,0.98) 0%, rgba(62,39,35,1) 100%)'
          }}
        />
        
        {/* Particle Count Overlay */}
        <div className="absolute top-4 left-4 bg-black/80 backdrop-blur border border-yellow-400/30 rounded px-3 py-2">
          <div className="text-yellow-300 text-sm font-bold">
            🎯 Particles: {machineState.particleCount}
          </div>
          <div className="text-yellow-400/70 text-xs">
            ⚡ Power: {Math.floor(machineState.power)}%
          </div>
        </div>
      </div>
      
      {/* Mobile-Responsive Controls */}
      <div className="mt-4 space-y-3">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {Object.entries(dimensions).map(([key, value]) => (
            <div key={key} className="bg-black/20 border border-yellow-400/30 rounded p-2">
              <label className="text-yellow-300 text-xs font-medium block mb-1">
                {key === 'r' ? '⚙️ R' :
                 key === 'theta' ? '🎯 θ' :
                 key === 'phi' ? '⚛️ φ' :
                 key === 't' ? '📊 T' :
                 key === 'f' ? '💻 F' :
                 key === 'u' ? '👑 U' :
                 '∞'}
              </label>
              <input
                type="range"
                value={value}
                onChange={(e) => updateDimension(key, parseFloat(e.target.value))}
                min={key === 'theta' ? 0 : key === 'phi' ? 0 : key === 'f' ? 40 : 0.1}
                max={key === 'theta' ? 360 : key === 'phi' ? 180 : key === 'f' ? 1000 : 5}
                step={key === 'theta' || key === 'phi' || key === 'f' ? 1 : 0.001}
                className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer"
              />
              <div className="text-yellow-400/70 text-xs mt-1">
                {typeof value === 'number' ? 
                  (key === 'theta' || key === 'phi' ? Math.floor(value) + '°' : value.toFixed(3))
                  : value}
              </div>
            </div>
          ))}
        </div>
      </div>
      
      <style jsx>{`
        input[type="range"]::-webkit-slider-thumb {
          appearance: none;
          width: 16px;
          height: 16px;
          border-radius: 50%;
          background: linear-gradient(45deg, #fbbf24, #f59e0b);
          cursor: pointer;
          box-shadow: 0 0 10px rgba(251, 191, 36, 0.5);
        }
        input[type="range"]::-moz-range-thumb {
          width: 16px;
          height: 16px;
          border-radius: 50%;
          background: linear-gradient(45deg, #fbbf24, #f59e0b);
          cursor: pointer;
          border: none;
        }
      `}</style>
    </div>
  );
};

export default GrantSteampunkEngine;